import type { SerializedNode, ConversionContext, RGBA, Gradient } from '../types';
import type { BackgroundImageMode, BackgroundPosition } from '../../types/layout';
import { getAttribute } from '../parser';
import { parseColor, parsePadding, parseInteger } from '../utils';
import { PAGE_DEFAULTS } from '../utils/defaults';
import { PAGE_NODE_NAME } from '../../utils/constants';
import { checkIsGradient } from '../../utils/styleHelpers';

type VideoMode = 'fill' | 'fit';

const BACKGROUND_IMAGE_MODES: BackgroundImageMode[] = ['fill', 'fit', 'stretch', 'tile'];
const BACKGROUND_POSITIONS: BackgroundPosition[] = [
  'left top',
  'center top',
  'right top',
  'left center',
  'center center',
  'right center',
  'left bottom',
  'center bottom',
  'right bottom',
];
const VIDEO_MODES: VideoMode[] = ['fill', 'fit'];

/**
 * Map a string value to a valid BackgroundImageMode
 */
const mapBgImageMode = (value: string | null): BackgroundImageMode => {
  if (!value) return PAGE_DEFAULTS.bgImageMode;
  const normalized = value.toLowerCase().trim();
  return BACKGROUND_IMAGE_MODES.includes(normalized as BackgroundImageMode)
    ? (normalized as BackgroundImageMode)
    : PAGE_DEFAULTS.bgImageMode;
};

/**
 * Map a string value to a valid BackgroundPosition
 */
const mapBgImagePosition = (value: string | null): BackgroundPosition => {
  if (!value) return PAGE_DEFAULTS.bgImagePosition;
  const normalized = value.toLowerCase().trim();
  return BACKGROUND_POSITIONS.includes(normalized as BackgroundPosition)
    ? (normalized as BackgroundPosition)
    : PAGE_DEFAULTS.bgImagePosition;
};

/**
 * Map a string value to a valid VideoMode
 */
const mapVideoMode = (value: string | null): VideoMode => {
  if (!value) return PAGE_DEFAULTS.bgVideoMode;
  const normalized = value.toLowerCase().trim();
  return VIDEO_MODES.includes(normalized as VideoMode)
    ? (normalized as VideoMode)
    : PAGE_DEFAULTS.bgVideoMode;
};

/**
 * Valid breakpoint attributes for the <page> element
 */
export const BREAKPOINT_PAGE_ATTRIBUTES = ['padding', 'gap', 'maxWidth'] as const;

/**
 * Parse tablet-* and mobile-* prefixed attributes for page element
 */
const parsePageBreakpointAttributes = (
  element: Element,
): { tablet: Record<string, unknown>; mobile: Record<string, unknown> } => {
  const tablet: Record<string, unknown> = {};
  const mobile: Record<string, unknown> = {};
  const attrs = Array.from(element.attributes);

  for (const attr of attrs) {
    let prefix: 'tablet' | 'mobile' | null = null;
    let propName: string | null = null;

    if (attr.name.startsWith('tablet-')) {
      prefix = 'tablet';
      propName = attr.name.slice(7);
    } else if (attr.name.startsWith('mobile-')) {
      prefix = 'mobile';
      propName = attr.name.slice(7);
    }

    if (!prefix || !propName) continue;
    if (!(BREAKPOINT_PAGE_ATTRIBUTES as readonly string[]).includes(propName)) continue;

    const target = prefix === 'tablet' ? tablet : mobile;

    switch (propName) {
      case 'padding':
        target.padding = parsePadding(attr.value, PAGE_DEFAULTS.padding);
        break;
      case 'gap':
        target.gap = parseInteger(attr.value, PAGE_DEFAULTS.gap);
        break;
      case 'maxWidth':
        target.maxWidth = attr.value;
        break;
    }
  }

  return { tablet, mobile };
};

/**
 * Transform a <page> XML element into a CraftJS Page node
 *
 * @param element - The <page> XML element
 * @param context - Conversion context
 * @param childIds - Array of child node IDs
 * @returns SerializedNode for the Page
 */
export const transformPage = (
  element: Element,
  _context: ConversionContext,
  childIds: string[],
): SerializedNode => {
  // Parse basic attributes with defaults
  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : PAGE_DEFAULTS.backgroundColor;

  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, PAGE_DEFAULTS.padding);

  const maxWidthAttr = getAttribute(element, 'maxWidth');
  const maxWidth = maxWidthAttr || PAGE_DEFAULTS.maxWidth;

  const gapAttr = getAttribute(element, 'gap');
  const gap = parseInteger(gapAttr, PAGE_DEFAULTS.gap);

  // Parse background image attributes
  const bgImageAttr = getAttribute(element, 'bgImage');
  const bgImage = bgImageAttr || PAGE_DEFAULTS.bgImage;

  const bgImageOpacityAttr = getAttribute(element, 'bgImageOpacity');
  const bgImageOpacity = parseInteger(bgImageOpacityAttr, PAGE_DEFAULTS.bgImageOpacity);

  const bgImageScaleAttr = getAttribute(element, 'bgImageScale');
  const bgImageScale = parseInteger(bgImageScaleAttr, PAGE_DEFAULTS.bgImageScale);

  const bgImageModeAttr = getAttribute(element, 'bgImageMode');
  const bgImageMode = mapBgImageMode(bgImageModeAttr);

  const bgImagePositionAttr = getAttribute(element, 'bgImagePosition');
  const bgImagePosition = mapBgImagePosition(bgImagePositionAttr);

  // Parse background video attributes
  const bgVideoUrlAttr = getAttribute(element, 'bgVideoUrl');
  const bgVideoUrl = bgVideoUrlAttr || PAGE_DEFAULTS.bgVideoUrl;

  const bgVideoOpacityAttr = getAttribute(element, 'bgVideoOpacity');
  const bgVideoOpacity = parseInteger(bgVideoOpacityAttr, PAGE_DEFAULTS.bgVideoOpacity);

  const bgVideoModeAttr = getAttribute(element, 'bgVideoMode');
  const bgVideoMode = mapVideoMode(bgVideoModeAttr);

  const bgVideoEmbedCodeAttr = getAttribute(element, 'bgVideoEmbedCode');
  const bgVideoEmbedCode = bgVideoEmbedCodeAttr || PAGE_DEFAULTS.bgVideoEmbedCode;

  // Parse breakpoint overrides
  const { tablet: tabletOverrides, mobile: mobileOverrides } =
    parsePageBreakpointAttributes(element);

  // Determine bgGradient/bgSolid for inspector panel color field
  let bgGradient: Gradient | undefined;
  let bgSolid: RGBA | undefined;
  if (backgroundColor && typeof backgroundColor !== 'string') {
    if (checkIsGradient(backgroundColor)) {
      bgGradient = backgroundColor as Gradient;
    } else {
      bgSolid = backgroundColor as RGBA;
    }
  }

  // Build props object
  const props: Record<string, unknown> = {
    fillSpace: PAGE_DEFAULTS.fillSpace,
    padding,
    backgroundColor,
    ...(bgGradient && { bgGradient }),
    ...(bgSolid && { bgSolid }),
    maxWidth,
    gap,
    bgImageScale,
    bgImageOpacity,
    bgImageMode,
    bgImagePosition,
    bgVideoSourceType: PAGE_DEFAULTS.bgVideoSourceType,
    bgVideoMode,
    bgVideoOpacity,
    tablet: { ...PAGE_DEFAULTS.tablet, ...tabletOverrides },
    mobile: { ...PAGE_DEFAULTS.mobile, ...mobileOverrides },
  };

  // Only include bgImage related props if bgImage is set
  if (bgImage) {
    props.bgImage = bgImage;
  }

  // Include bgVideoUrl and related props only if bgVideoUrl is set
  if (bgVideoUrl) {
    props.bgVideoUrl = bgVideoUrl;
    if (bgVideoEmbedCode) {
      props.bgVideoEmbedCode = bgVideoEmbedCode;
    }
  }

  return {
    type: { resolvedName: PAGE_NODE_NAME },
    isCanvas: true,
    props,
    displayName: PAGE_NODE_NAME,
    custom: {
      displayName: 'Landing Page',
      isLocked: false,
    },
    hidden: false,
    nodes: childIds,
    linkedNodes: {},
    parent: null,
  };
};

/**
 * Valid attributes for the <page> element
 */
export const PAGE_ATTRIBUTES = [
  'bg',
  'bgImage',
  'padding',
  'maxWidth',
  'gap',
  // Background image attributes
  'bgImageOpacity',
  'bgImageScale',
  'bgImageMode',
  'bgImagePosition',
  // Background video attributes
  'bgVideoUrl',
  'bgVideoOpacity',
  'bgVideoMode',
  'bgVideoEmbedCode',
] as const;

/**
 * Check if an attribute is valid for the <page> element
 */
export const isValidPageAttribute = (attr: string): boolean => {
  // Check base attributes
  if ((PAGE_ATTRIBUTES as readonly string[]).includes(attr)) {
    return true;
  }

  // Check breakpoint-prefixed attributes (tablet-padding, mobile-gap, etc.)
  let propName: string | null = null;
  if (attr.startsWith('tablet-')) {
    propName = attr.slice(7);
  } else if (attr.startsWith('mobile-')) {
    propName = attr.slice(7);
  }

  if (propName) {
    return (BREAKPOINT_PAGE_ATTRIBUTES as readonly string[]).includes(propName);
  }

  return false;
};
